// Minimal, dependency-free Server-Sent Events (SSE) frame parser.
//
// The tournament live stream is consumed with `fetch` + a `ReadableStream`
// reader (see `apiClient.subscribeTournamentEvents`) rather than the browser's
// `EventSource`, because `EventSource` cannot send an `Authorization` header —
// and we must not put the bearer token in the URL, where it would be captured in
// server / proxy access logs (e.g. Cloud Run request logs). `fetch` streaming
// gives us header auth and full control over reconnection, at the cost of having
// to split the wire format ourselves. This module is that split, kept pure so it
// is unit-testable without a network or a DOM.
//
// Wire format (the subset we emit, see `server/tournamentEvents.ts`): events are
// separated by a blank line; within an event, `event:` names it and `data:`
// carries the payload; a line starting with `:` is a comment (heartbeat) and a
// `retry:` line is a reconnect hint — both ignored here.

export interface SseEvent {
  /** The `event:` field, defaulting to `message` when absent. */
  event: string;
  /** The joined `data:` field(s). */
  data: string;
}

/**
 * Stateful, incremental SSE parser. Feed it text chunks as they arrive off the
 * stream (which may split an event across chunk boundaries); it returns the
 * complete events decoded so far and buffers any partial trailing event.
 */
export class SseParser {
  private buffer = '';

  push(chunk: string): SseEvent[] {
    // Normalize CR / CRLF line endings to LF so framing is uniform.
    this.buffer += chunk.replace(/\r\n?/g, '\n');
    const events: SseEvent[] = [];
    let sep: number;
    while ((sep = this.buffer.indexOf('\n\n')) !== -1) {
      const raw = this.buffer.slice(0, sep);
      this.buffer = this.buffer.slice(sep + 2);
      const event = parseFrame(raw);
      if (event) events.push(event);
    }
    return events;
  }
}

/** Parse one event block (the text between blank lines) into an SseEvent. */
function parseFrame(raw: string): SseEvent | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of raw.split('\n')) {
    // Blank lines and comments (`: ping`) carry no fields.
    if (line === '' || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    // A single leading space after the colon is part of the framing, not data.
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }
  // Frames with no data (e.g. a lone `retry:` hint or a comment-only block)
  // carry nothing actionable.
  if (data.length === 0) return null;
  return { event, data: data.join('\n') };
}
